import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export const BROKER_FIELDS = ["name", "address", "contact_name", "phone", "email", "mc_number", "notes"];
const blank = () => Object.fromEntries(BROKER_FIELDS.map((k) => [k, ""]));

export const brokerKey = (s) => (s || "").toLowerCase().replace(/&/g, " and ").replace(/[^a-z0-9 ]/g, " ")
  .replace(/\b(inc|incorporated|llc|l l c|corp|corporation|co|company|ltd|limited|lp|llp)\b/g, " ").replace(/\s+/g, " ").trim();

export const brokerSummary = (b) => [b.address, b.contact_name && `Attn: ${b.contact_name}`, b.phone, b.email].filter(Boolean).join(" · ");

// broker: null = closed; {} or {name,...} = create (prefill); {id,...} = edit
export const BrokerDialog = ({ broker, api, onClose, onSaved }) => {
  const [f, setF] = useState(blank());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (broker) { setF({ ...blank(), ...Object.fromEntries(BROKER_FIELDS.map((k) => [k, broker[k] || ""])) }); setError(""); }
  }, [broker]);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const save = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = broker.id ? await api.patch(`/brokers/${broker.id}`, f) : await api.post("/brokers", f);
      toast.success(broker.id ? "Broker updated" : `${data.name} saved`);
      onSaved(data);
      onClose();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(Array.isArray(d) ? d.map((x) => x.msg).join(", ") : d || "Could not save broker");
    } finally {
      setBusy(false);
    }
  };
  const MAX = { name: 200, contact_name: 120, phone: 40, email: 120, mc_number: 30 };
  const input = (k, label, ph) => (
    <div><Label htmlFor={`broker-${k}`}>{label}</Label><Input id={`broker-${k}`} value={f[k]} onChange={set(k)} placeholder={ph} maxLength={MAX[k]} className="mt-1" data-testid={`broker-${k}-input`} /></div>
  );
  return (
    <Dialog open={!!broker} onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent className="max-w-lg" data-testid="broker-dialog">
        <DialogHeader>
          <DialogTitle>{broker?.id ? "Edit broker" : "New broker"}</DialogTitle>
          <DialogDescription>{broker?.id ? "Changes apply to this broker's draft and future invoices." : "Saved details fill the bill-to block on invoices."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
          {input("name", "Company name *", "e.g. Echo Global Logistics")}
          <div><Label htmlFor="broker-address">Billing address</Label><Textarea id="broker-address" rows={2} maxLength={300} value={f.address} onChange={set("address")} placeholder={"Street\nCity, ST ZIP"} className="mt-1" data-testid="broker-address-input" /></div>
          <div className="grid sm:grid-cols-2 gap-3">
            {input("contact_name", "Contact name", "Billing / AP contact")}
            {input("phone", "Phone", "(555) 555-0100")}
            {input("email", "Email", "ap@broker.com")}
            {input("mc_number", "MC number (optional)", "MC-123456")}
          </div>
          <div><Label htmlFor="broker-notes">Billing notes</Label><Textarea id="broker-notes" rows={2} maxLength={1000} value={f.notes} onChange={set("notes")} placeholder="e.g. Email invoices with POD to ap@… within 7 days" className="mt-1" data-testid="broker-notes-input" /></div>
          {error && <p className="text-sm text-rose-600" data-testid="broker-error">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={!f.name.trim() || busy} className="bg-[#002FA7] hover:bg-[#001A75]" data-testid="broker-save-btn">{busy ? "Saving…" : "Save broker"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
